// Bazaraki search scanner - fetches and parses search results from Bazaraki API
// Extracted from App.js handleScanSearch to support multi-platform search scanning

import { fetchViaProxies } from './proxyFetch';

/**
 * Scan a Bazaraki search API URL and return normalized results.
 * Supports multiple Bazaraki response formats:
 *   - results.results[] (standard search)
 *   - results.geometry[] (map/geo search)
 *   - results.data[] (alternative format)
 *   - results.items[] (list format)
 *   - results.features[] (GeoJSON format)
 *
 * @param {string} searchUrl - Bazaraki search API URL
 * @returns {Promise<Array<{id: string, title: string, price: number|string, url: string}>>}
 */
export const scanBazarakiSearch = async (searchUrl) => {
  let results;

  try {
    results = await fetchViaProxies(searchUrl, {
      timeout: 15000,
      responseType: 'json',
      label: 'Bazaraki Search',
      validate: (data) => data && typeof data === 'object',
    });
  } catch {
    throw new Error(
      'All proxy services are currently unavailable. Please try again later.'
    );
  }

  if (!results) {
    throw new Error('Failed to fetch search results');
  }

  // Parse the various Bazaraki API response formats
  let rawItems = [];

  if (results.results && Array.isArray(results.results)) {
    rawItems = results.results.map((item) => ({
      id: item.id,
      title: item.title,
      price: item.price,
      url_main: item.absolute_url,
    }));
  } else if (results.geometry && Array.isArray(results.geometry)) {
    rawItems = results.geometry.map((geoItem) => {
      const props = geoItem.properties || geoItem;
      return {
        id: props.id || props.advert_id,
        title: props.title || props.name,
        price: props.price,
        url_main:
          props.url_main ||
          (props.id ? `/adv/${props.id}_placeholder/` : null),
      };
    });
  } else if (results.data && Array.isArray(results.data)) {
    rawItems = results.data.map((item) => ({
      id: item.id,
      title: item.title,
      price: item.price,
      url_main:
        item.url_main || (item.id ? `/adv/${item.id}_placeholder/` : null),
    }));
  } else if (results.items && Array.isArray(results.items)) {
    rawItems = results.items;
  } else if (results.features && Array.isArray(results.features)) {
    rawItems = results.features.map((feature) => ({
      id: feature.properties?.id || feature.id,
      title: feature.properties?.title || feature.properties?.name,
      price: feature.properties?.price,
      url_main:
        feature.properties?.url_main ||
        (feature.properties?.id
          ? `/adv/${feature.properties.id}_placeholder/`
          : null),
    }));
  } else {
    console.warn(
      '[Bazaraki Search] Could not find a known items array in response:',
      Object.keys(results)
    );
  }

  // Normalize to standard format with full URLs
  // Keep price as-is from the API (could be number or formatted string like "1.300")
  return rawItems
    .map((item) => ({
      id: String(item.id),
      title: item.title || '',
      price: item.price ?? 0,
      url: `https://www.bazaraki.com${item.url_main || `/adv/${item.id}_placeholder/`}`,
    }))
    .filter(
      (item) =>
        item.id && item.id !== 'undefined' && item.id !== 'null'
    );
};
